import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material'
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { apiGet } from '../../generic/Api_Functions'
import { RENDERABLE_IMAGE_INPUT_ACCEPT, RENDERABLE_IMAGE_FORMATS_LABEL } from './renderableImageFormats'
import { NA } from './requestFormNaDefaults'

const PrototypeRequestForm = ({ formData, setFormData, remoteErrors, onSubmit, isSubmitting }) => {
  const [materialTypes, setMaterialTypes] = useState([])
  const [errors, setErrors] = useState(remoteErrors || {})
  const [node, setNode] = useState(null)
  const fileInputAisladoRef = useRef(null)
  const fileInputEnsambladoRef = useRef(null)

  // Story 115: every control still starts empty. The only change is that the in-scope dropdowns
  // offer N/A as a last option, for the requester who wants to state that a question does not
  // apply. Nothing is preloaded - the N/A of the printed form is put there by Sun, which prints
  // it in every report cell that arrives empty.

  useEffect(() => {
    const materialTypesFetched = async () => {
      const data = await apiGet('materialTypes')
      setMaterialTypes(data)
    }
    materialTypesFetched()
  }, [])

  useEffect(() => {
    const loadNode = async () => {
      const response = await apiGet('cluster/getNodeName')
      setNode(response.message)
    }
    loadNode()
  }, [])

  useEffect(() => {
    setErrors(remoteErrors || {})
  }, [remoteErrors])

  const handleRemoveImage = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }))

    if (fieldName === 'imagenComponenteAislado' && fileInputAisladoRef.current) {
      fileInputAisladoRef.current.value = ''
    }
    if (fieldName === 'imagenComponenteEnsamblado' && fileInputEnsambladoRef.current) {
      fileInputEnsambladoRef.current.value = ''
    }
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target
    // Clear error when the user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }

    if (files) {
      setFormData(prev => ({ ...prev, [name]: files[0] }))
      // Clean file error if one is selected
      if (name === 'imagenComponenteAislado' && files[0]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    if (name.startsWith('solicitante.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        solicitante: { ...prev.solicitante, [key]: value }
      }))
      // Clear specific error from nested field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    if (name.startsWith('dimensionesMaximas.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        dimensionesMaximas: { ...prev.dimensionesMaximas, [key]: value }
      }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (date, field) => {
    setFormData(prev => ({ ...prev, [field]: date }))
    // Clear date error if a
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleMaterialChange = (e) => {
    const selectedCode = e.target.value
    const selectedMaterial = materialTypes.find(m => m.code === selectedCode)
    setFormData(prev => ({
      ...prev,
      materialPropuesto: selectedMaterial || { id: '', code: '', name: '' }
    }))
    setFormData(prev => ({ ...prev, materialPropuestoCode: selectedCode }))
    // Clear error
    if (errors.materialPropuesto) {
      setErrors(prev => ({ ...prev, materialPropuesto: '' }))
    }
  }

  const SectionTitle = ({ children }) => (
    <Grid item xs={12}>
      <Typography
        variant='h6'
        sx={{
          color: '#7FE1A7',
          borderBottom: '2px solid #444',
          paddingBottom: 1,
          marginBottom: 2
        }}
      >
        {children}
      </Typography>
    </Grid>
  )

  const textFieldStyles = {
    '& .MuiInputBase-input': { color: '#e3e3e3' },
    '& .MuiInputLabel-root': {
      color: '#e3e3e3',
      '&.Mui-error': { color: '#F68D76' }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#AAAAAA' },
      '&:hover fieldset': { borderColor: '#7FE1A7' },
      '&.Mui-error fieldset': { borderColor: '#F68D76' }
    }
  }

  const selectStyles = {
    color: '#e3e3e3',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#AAAAAA' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7FE1A7' },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#F68D76' }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        component='form'
        onSubmit={onSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 3,
          backgroundColor: '#1b1b1b',
          color: '#e3e3e3',
          borderRadius: 2,
          maxWidth: 1000,
          margin: '0 auto'
        }}
      >

        {/* SECCIÓN: IDENTIFICACIÓN */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>IDENTIFICACIÓN</SectionTitle>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Elaborado por'
                name='elaboradoPor'
                value={formData.elaboradoPor}
                onChange={handleChange}
                placeholder='Nombre y apellidos del responsable'
                sx={textFieldStyles}
                error={!!errors.elaboradoPor}
                helperText={errors.elaboradoPor}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='* Codigo proyecto'
                name='codigoProyecto'
                value={formData.codigoProyecto}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.codigoProyecto}
                helperText={errors.codigoProyecto}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Nombre proyecto'
                name='nombreProyecto'
                value={formData.nombreProyecto}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                label='* Fecha objetivo'
                value={formData.fechaObjetivo}
                onChange={(date) => handleDateChange(date, 'fechaObjetivo')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: textFieldStyles,
                    error: !!errors.fechaObjetivo,
                    helperText: errors.fechaObjetivo,
                    InputLabelProps: {
                      sx: {
                        color: errors.fechaObjetivo ? '#F68D76' : '#e3e3e3'
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Prioridad</InputLabel>
                <Select
                  name='prioridad'
                  value={formData.prioridad}
                  onChange={handleChange}
                  label='Prioridad'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar prioridad...</MenuItem>
                  <MenuItem key={1} value='BAJA'>BAJA</MenuItem>
                  <MenuItem key={2} value='MEDIA'>MEDIA</MenuItem>
                  <MenuItem key={3} value='ALTA'>ALTA</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>

                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Solicitante POC / Empresa - Nombre/apellidos'
                name='solicitanteNombre'
                value={formData.solicitanteNombre}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.solicitanteNombre}
                helperText={errors.solicitanteNombre}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type='email'
                label='Solicitante POC / Empresa - Email'
                name='solicitanteEmail'
                value={formData.solicitanteEmail}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Denominación (nombre)'
                name='denominacion'
                value={formData.denominacion}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.denominacion}
                helperText={errors.denominacion}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='* Pieza (codigo)'
                name='pieza'
                value={formData.pieza}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.pieza}
                helperText={errors.pieza}
                inputProps={{ maxLength: 40 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='NOC/NIN'
                name='nocNin'
                value={formData.nocNin}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='FORE'
                name='fore'
                value={formData.fore}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Sistema'
                name='sistema'
                value={formData.sistema}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Disponibilidad comercial</InputLabel>
                <Select
                  name='disponibilidadComercial'
                  value={formData.disponibilidadComercial}
                  onChange={handleChange}
                  label='Disponibilidad comercial'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Nº Plano'
                name='numeroPlano'
                value={formData.numeroPlano}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Norma específica'
                name='normaEspecifica'
                value={formData.normaEspecifica}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='* Nº piezas solicitadas'
                name='numeroPiezasSolicitadas'
                value={formData.numeroPiezasSolicitadas}
                onChange={(e) => {
                  const value = e.target.value
                  if (/^\d*$/.test(value)) {
                    setFormData({
                      ...formData,
                      numeroPiezasSolicitadas: value
                    })
                  }
                }}
                sx={textFieldStyles}
                error={!!errors.numeroPiezasSolicitadas}
                helperText={errors.numeroPiezasSolicitadas}
                inputProps={{
                  inputMode: 'numeric', // mobile numeric keyboard
                  pattern: '[0-9]*'
                }}
              />

            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Causa reemplazo'
                name='causaReemplazo'
                value={formData.causaReemplazo}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Descripción de la entrega (planos, muestra, etc)'
                name='descripcionEntrega'
                value={formData.descripcionEntrega}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* SECCIÓN: CENTROS ESPECÍFICOS */}
        {node?.toLowerCase() === 'cordoba' || node?.toLowerCase() === 'córdoba' || node?.toLowerCase() === 'rota' || node?.toLowerCase() === 'albacete'
          ? (
            <>
              <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
                <Grid container spacing={2}>
                  <SectionTitle>ESPECÍFICO CESFARE</SectionTitle>

                  {/* CESFARE-Rota */}
                  {node?.toLowerCase() === 'rota'
                    ? (
                      <>
                        <Grid item xs={12}>
                          <SectionTitle>CESFARE-Rota</SectionTitle>
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label='Clase de buque'
                            name='claseBuque'
                            value={formData.claseBuque}
                            onChange={handleChange}
                            sx={textFieldStyles}
                            inputProps={{ maxLength: 70 }}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label='Nombre del buque'
                            name='nombreBuque'
                            value={formData.nombreBuque}
                            onChange={handleChange}
                            sx={textFieldStyles}
                            inputProps={{ maxLength: 70 }}
                          />
                        </Grid>
                      </>)
                    : null}

                  {/* CESFARE-Córdoba */}
                  {node?.toLowerCase() === 'cordoba' || node?.toLowerCase() === 'córdoba'
                    ? (
                      <>
                        <Grid item xs={12}>
                          <SectionTitle>CESFARE-Córdoba</SectionTitle>
                        </Grid>

                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={{ color: '#e3e3e3' }}>Pertenece al CUF</InputLabel>
                            <Select
                              name='perteneceCUF'
                              value={formData.perteneceCUF}
                              onChange={handleChange}
                              label='Pertenece al CUF'
                              sx={selectStyles}
                            >
                              <MenuItem value=''>Seleccionar...</MenuItem>
                              <MenuItem value='Si'>Si</MenuItem>
                              <MenuItem value='No'>No</MenuItem>
                              <MenuItem value={NA}>{NA}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label='NOC del CUF'
                            name='nocCUF'
                            value={formData.nocCUF}
                            onChange={handleChange}
                            sx={textFieldStyles}
                            inputProps={{ maxLength: 35 }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label='Denominación CUF'
                            name='denominacionCUF'
                            value={formData.denominacionCUF}
                            onChange={handleChange}
                            sx={textFieldStyles}
                            inputProps={{ maxLength: 70 }}
                          />
                        </Grid>
                      </>)
                    : null}

                  {/* CESFARE Albacete */}
                  {node?.toLowerCase() === 'albacete'
                    ? (
                      <>
                        <Grid item xs={12}>
                          <SectionTitle>CESFARE Albacete</SectionTitle>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label='Clase avión'
                            name='claseAvion'
                            value={formData.claseAvion}
                            onChange={handleChange}
                            sx={textFieldStyles}
                            inputProps={{ maxLength: 70 }}
                          />
                        </Grid>
                      </>)
                    : null}

                </Grid>
              </Paper>
            </>)
          : null}

        {/* SECCIÓN: CARÁCTERÍSTICAS GENERALES */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>CARÁCTERÍSTICAS GENERALES</SectionTitle>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label='Ancho [mm]'
                name='dimensionesMaximasAncho'
                value={formData.dimensionesMaximasAncho}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label='Fondo [mm]'
                name='dimensionesMaximasFondo'
                value={formData.dimensionesMaximasFondo}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label='Alto [mm]'
                name='dimensionesMaximasAlto'
                value={formData.dimensionesMaximasAlto}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Peso [g]'
                name='peso'
                value={formData.peso}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Peso relevante</InputLabel>
                <Select
                  name='pesoRelevante'
                  value={formData.pesoRelevante}
                  onChange={handleChange}
                  label='Peso relevante'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Tolerancias'
                name='tolerancias'
                value={formData.tolerancias}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Diseño modificable</InputLabel>
                <Select
                  name='disenoModificable'
                  value={formData.disenoModificable}
                  onChange={handleChange}
                  label='Diseño modificable'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Certificación</InputLabel>
                <Select
                  name='certificacion'
                  value={formData.certificacion}
                  onChange={handleChange}
                  label='Certificación'
                  sx={selectStyles}
                  inputProps={{ maxLength: 35 }}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Flexibilidad</InputLabel>
                <Select
                  name='flexibilidad'
                  value={formData.flexibilidad}
                  onChange={handleChange}
                  label='Flexibilidad'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Alta'>Alta</MenuItem>
                  <MenuItem value='Media'>Media</MenuItem>
                  <MenuItem value='Baja'>Baja</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Color'
                name='color'
                value={formData.color}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Color relevante</InputLabel>
                <Select
                  name='colorRelevante'
                  value={formData.colorRelevante}
                  onChange={handleChange}
                  label='Color relevante'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Descripción uso'
                name='descripcionUso'
                value={formData.descripcionUso}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Posición de la pieza dentro del sistema'
                name='posicionPieza'
                value={formData.posicionPieza}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Forma parte de un ensamblaje</InputLabel>
                <Select
                  name='formaParteEnsamblaje'
                  value={formData.formaParteEnsamblaje}
                  onChange={handleChange}
                  label='Forma parte de un ensamblaje'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>¿Se requieren todos los componentes?</InputLabel>
                <Select
                  name='requierenTodosComponentes'
                  value={formData.requierenTodosComponentes}
                  onChange={handleChange}
                  label='¿Se requieren todos los componentes?'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* SECCIÓN: MATERIALES */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>MATERIALES</SectionTitle>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  rows={3}
                  label='Material actual'
                  name='materialActual'
                  value={formData.materialActual}
                  onChange={handleChange}
                  sx={textFieldStyles}
                  inputProps={{ maxLength: 80 }}
                />

              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth error={!!errors.materialPropuesto}>
                <InputLabel sx={{ color: errors.materialPropuesto ? '#F68D76' : '#e3e3e3' }}>
                  Material propuesto *
                </InputLabel>
                <Select
                  name='materialPropuesto'
                  value={formData.materialPropuesto.code || ''}
                  onChange={handleMaterialChange} // Usar el manejador personalizado
                  label='* Material propuesto'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar material...</MenuItem>
                  {materialTypes.map(m => (
                    <MenuItem key={m.id} value={m.code}>
                      {m.name} {m.code && `(${m.code})`}
                    </MenuItem>
                  ))}
                </Select>
                {errors.materialPropuesto && (
                  <FormHelperText error>{errors.materialPropuesto}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Tratamiento superficial</InputLabel>
                <Select
                  name='tratamientoSuperficial'
                  value={formData.tratamientoSuperficial}
                  onChange={handleChange}
                  label='Tratamiento superficial'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Tipo tratamiento superficial'
                name='tipoTratamientoSuperficial'
                value={formData.tipoTratamientoSuperficial}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 35 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Tratamiento térmico</InputLabel>
                <Select
                  name='tratamientoTermico'
                  value={formData.tratamientoTermico}
                  onChange={handleChange}
                  label='Tratamiento térmico'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Tipo tratamiento térmico'
                name='tipoTratamientoTermico'
                value={formData.tipoTratamientoTermico}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 35 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>¿Está en contacto con partes metálicas?</InputLabel>
                <Select
                  name='contactoPartesMetalicas'
                  value={formData.contactoPartesMetalicas}
                  onChange={handleChange}
                  label='¿Está en contacto con partes metálicas?'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Dureza superficial requerida'
                name='durezaSuperficialRequerida'
                value={formData.durezaSuperficialRequerida}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Tecnología de fabricación'
                name='tecnologiaFabricacion'
                value={formData.tecnologiaFabricacion}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 35 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Probetas'
                name='probetas'
                value={formData.probetas}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* SECCIÓN: CONDICIONES DE TRABAJO */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>CONDICIONES DE TRABAJO</SectionTitle>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Temperatura</InputLabel>
                <Select
                  name='temperatura'
                  value={formData.temperatura}
                  onChange={handleChange}
                  label='Temperatura'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar rango...</MenuItem>
                  <MenuItem value='0-50C'>0-50C</MenuItem>
                  <MenuItem value='50-100C'>50-100C</MenuItem>
                  <MenuItem value='>100C'>&gt;100C</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Impactos</InputLabel>
                <Select
                  name='impactos'
                  value={formData.impactos}
                  onChange={handleChange}
                  label='Impactos'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Resistencia mecánica</InputLabel>
                <Select
                  name='resistenciaMecanica'
                  value={formData.resistenciaMecanica}
                  onChange={handleChange}
                  label='Resistencia mecánica'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar nivel...</MenuItem>
                  <MenuItem value='Alta'>Alta</MenuItem>
                  <MenuItem value='Media'>Media</MenuItem>
                  <MenuItem value='Baja'>Baja</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Transparencia necesaria</InputLabel>
                <Select
                  name='transparenciaNecesaria'
                  value={formData.transparenciaNecesaria}
                  onChange={handleChange}
                  label='Transparencia necesaria'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Contacto con químicos</InputLabel>
                <Select
                  name='contactoQuimicos'
                  value={formData.contactoQuimicos}
                  onChange={handleChange}
                  label='Contacto con químicos'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar tipo...</MenuItem>
                  <MenuItem value='lubricantes/aceites'>lubricantes/aceites</MenuItem>
                  <MenuItem value='gasolina'>gasolina</MenuItem>
                  <MenuItem value='ácidos'>ácidos</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Intemperie</InputLabel>
                <Select
                  name='intemperie'
                  value={formData.intemperie}
                  onChange={handleChange}
                  label='Intemperie'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Uso contacto humano</InputLabel>
                <Select
                  name='usoContactoHumano'
                  value={formData.usoContactoHumano}
                  onChange={handleChange}
                  label='Uso contacto humano'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar...</MenuItem>
                  <MenuItem value='Si'>Si</MenuItem>
                  <MenuItem value='No'>No</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Zonas críticas'
                name='zonasCriticas'
                value={formData.zonasCriticas}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* SECCIÓN: IMÁGENES */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>IMÁGENES</SectionTitle>

            {/* Imagen componente aislado */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.imagenComponenteAislado}>
                <Button
                  variant='outlined'
                  component='label'
                  fullWidth
                  sx={{
                    color: errors.imagenComponenteAislado ? '#F68D76' : '#e3e3e3',
                    borderColor: errors.imagenComponenteAislado ? '#F68D76' : '#AAAAAA',
                    '&:hover': {
                      borderColor: errors.imagenComponenteAislado ? '#F68D76' : '#7FE1A7'
                    }
                  }}
                >
                  Imagen componente aislado (obligatorio) *
                  <input
                    type='file'
                    name='imagenComponenteAislado'
                    hidden
                    ref={fileInputAisladoRef}
                    accept={RENDERABLE_IMAGE_INPUT_ACCEPT}
                    onChange={handleChange}
                  />
                </Button>
                <FormHelperText sx={{ mt: 1 }}>
                  Formatos admitidos: {RENDERABLE_IMAGE_FORMATS_LABEL}
                </FormHelperText>
                {errors.imagenComponenteAislado && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.imagenComponenteAislado}
                  </FormHelperText>
                )}
                {formData.imagenComponenteAislado && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <FormHelperText sx={{ color: '#7FE1A7', mb: 0 }}>
                        Archivo seleccionado: {formData.imagenComponenteAislado.name}
                      </FormHelperText>
                      <Button
                        size='small'
                        variant='outlined'
                        color='error'
                        onClick={() => handleRemoveImage('imagenComponenteAislado')}
                        sx={{ minWidth: 'auto', padding: '2px 8px' }}
                      >
                        ✕
                      </Button>
                    </Box>
                    <img
                      src={URL.createObjectURL(formData.imagenComponenteAislado)}
                      alt='Vista previa componente aislado'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #AAAAAA'
                      }}
                    />
                  </Box>
                )}
              </FormControl>
            </Grid>

            {/* Imagen componente ensamblado */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Button
                  variant='outlined'
                  component='label'
                  fullWidth
                  sx={{
                    color: '#e3e3e3',
                    borderColor: '#AAAAAA',
                    '&:hover': { borderColor: '#7FE1A7' }
                  }}
                >
                  Imagen componente ensamblado (opcional)
                  <input
                    type='file'
                    name='imagenComponenteEnsamblado'
                    hidden
                    ref={fileInputEnsambladoRef}
                    accept={RENDERABLE_IMAGE_INPUT_ACCEPT}
                    onChange={handleChange}
                  />
                </Button>
                <FormHelperText sx={{ mt: 1 }}>
                  Formatos admitidos: {RENDERABLE_IMAGE_FORMATS_LABEL}
                </FormHelperText>
                {formData.imagenComponenteEnsamblado && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <FormHelperText sx={{ color: '#7FE1A7', mb: 0 }}>
                        Archivo seleccionado: {formData.imagenComponenteEnsamblado.name}
                      </FormHelperText>
                      <Button
                        size='small'
                        variant='outlined'
                        color='error'
                        onClick={() => handleRemoveImage('imagenComponenteEnsamblado')}
                        sx={{ minWidth: 'auto', padding: '2px 8px' }}
                      >
                        ✕
                      </Button>
                    </Box>
                    <img
                      src={URL.createObjectURL(formData.imagenComponenteEnsamblado)}
                      alt='Vista previa componente ensamblado'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #AAAAAA'
                      }}
                    />
                  </Box>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* BOTÓN ENVIAR */}
        <Button
          type='submit'
          variant='contained'
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#7FE1A7',
            color: '#000',
            fontWeight: 'bold',
            padding: '12px 24px',
            '&:hover': {
              backgroundColor: '#6bd095'
            }
          }}
        >
          Enviar Solicitud
        </Button>

      </Box>
    </LocalizationProvider>
  )
}

export default PrototypeRequestForm
